import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { analytics, campaigns as campaignsApi, type DashboardAnalytics, type Campaign } from "@/lib/api";
import Spinner from "@/components/Spinner";
import StatusBadge from "@/components/StatusBadge";
import { Mail, Eye, MousePointerClick, AlertTriangle, ArrowRight, TrendingUp } from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie,
} from "recharts";

const STAT_ACCENT: Record<string, string> = {
  coral:  "bg-coral/10 text-coral",
  teal:   "bg-teal/10 text-teal",
  purple: "bg-purple-50 text-purple-600",
  amber:  "bg-amber-50 text-amber-600",
};

function Stat({ label, value, icon: Icon, color }: { label: string; value: string | number; icon: React.ElementType; color: string }) {
  return (
    <div className="bg-white rounded-2xl border border-black/[0.06] p-5">
      <div className={`inline-flex p-2.5 rounded-xl mb-3 ${STAT_ACCENT[color]}`}>
        <Icon size={18} />
      </div>
      <p className="text-2xl font-bold text-ink leading-none">{value}</p>
      <p className="text-xs font-semibold text-slate uppercase tracking-wider mt-1.5">{label}</p>
    </div>
  );
}

const TABS = ["Overview", "Campaigns"] as const;
type Tab = typeof TABS[number];

export default function AnalyticsPage() {
  const [tab, setTab]             = useState<Tab>("Overview");
  const [data, setData]           = useState<DashboardAnalytics | null>(null);
  const [allCamps, setAllCamps]   = useState<Campaign[]>([]);
  const [ts, setTs]               = useState<Array<{ date: string; sent: number; opens: number; clicks: number }>>([]);
  const [days, setDays]           = useState(30);
  const [loading, setLoading]     = useState(true);
  const [campsLoading, setCampsLoading] = useState(false);
  const [drawerCamp, setDrawerCamp] = useState<Campaign | null>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([analytics.dashboard(), analytics.timeseries(days)])
      .then(([dash, tsSeries]) => {
        setData(dash);
        setTs(tsSeries.timeseries.map((d) => ({
          date:   new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
          sent:   parseInt(d.sent) || 0,
          opens:  parseInt(d.opens) || 0,
          clicks: parseInt(d.clicks) || 0,
        })));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => {
    if (tab !== "Campaigns" || allCamps.length) return;
    setCampsLoading(true);
    campaignsApi.list({ maxResults: 50 })
      .then((r) => setAllCamps(r.campaigns))
      .catch(console.error)
      .finally(() => setCampsLoading(false));
  }, [tab]);

  if (loading) return <Spinner className="h-screen" />;

  const perf  = data?.performance;
  const camps = data?.campaigns;

  const pieData = camps ? [
    { name: "Sent",      value: parseInt(camps.sent_campaigns)      || 0, color: "#FF5F45" },
    { name: "Active",    value: parseInt(camps.active_campaigns)    || 0, color: "#0A8F7F" },
    { name: "Draft",     value: parseInt(camps.draft_campaigns)     || 0, color: "#CBD5E1" },
    { name: "Scheduled", value: parseInt(camps.scheduled_campaigns) || 0, color: "#7C3AED" },
  ].filter((d) => d.value > 0) : [];

  const rates = [
    { label: "Open Rate",      value: parseFloat(perf?.open_rate  || "0"), color: "#FF5F45", bg: "from-coral/20"  },
    { label: "Click Rate",     value: parseFloat(perf?.click_rate || "0"), color: "#0A8F7F", bg: "from-teal/20"   },
    { label: "Click-to-Open",  value: perf && perf.total_opens > 0 ? (perf.total_clicks / perf.total_opens) * 100 : 0, color: "#7C3AED", bg: "from-purple-200" },
  ];

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur-md border-b border-black/[0.06] px-4 sm:px-6 lg:px-8 py-4">
        <h1 className="text-lg sm:text-xl font-bold text-ink">Analytics</h1>
        <p className="text-xs sm:text-sm text-slate mt-0.5">Performance across all campaigns</p>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto">

        {/* Metric strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Stat label="Total Sent"    value={Number(perf?.total_sent  || 0).toLocaleString()} icon={Mail}             color="coral"  />
          <Stat label="Total Opens"   value={Number(perf?.total_opens || 0).toLocaleString()} icon={Eye}              color="teal"   />
          <Stat label="Total Clicks"  value={Number(perf?.total_clicks|| 0).toLocaleString()} icon={MousePointerClick}color="purple" />
          <Stat label="Bounce Rate"   value={perf && perf.total_sent > 0 ? `${((perf.total_bounces / perf.total_sent)*100).toFixed(1)}%` : "0%"} icon={AlertTriangle} color="amber" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-black/[0.04] rounded-xl w-fit">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                tab === t ? "bg-white text-ink shadow-sm" : "text-slate hover:text-ink"
              }`}>
              {t}
            </button>
          ))}
        </div>

        {/* Overview tab */}
        {tab === "Overview" && (
          <div className="space-y-6">
            {/* Chart */}
            <div className="bg-white rounded-2xl border border-black/[0.06] p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                <h2 className="font-bold text-ink">Daily Activity</h2>
                <div className="flex gap-1.5 p-1 bg-black/[0.04] rounded-xl w-fit">
                  {[7, 14, 30].map((d) => (
                    <button key={d} onClick={() => setDays(d)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        days === d ? "bg-white text-ink shadow-sm" : "text-slate hover:text-ink"
                      }`}>
                      {d}d
                    </button>
                  ))}
                </div>
              </div>
              {ts.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={ts} barGap={3} margin={{ top: 0, right: 4, bottom: 0, left: -20 }}>
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#5A6872" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11, fill: "#5A6872" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "#0B1215", border: "none", borderRadius: 10, color: "white", fontSize: 12, padding: "8px 12px" }} />
                    <Bar dataKey="sent"   fill="#FF5F45" radius={[3,3,0,0]} name="Sent"   opacity={0.25} />
                    <Bar dataKey="opens"  fill="#FF5F45" radius={[3,3,0,0]} name="Opens"  />
                    <Bar dataKey="clicks" fill="#0A8F7F" radius={[3,3,0,0]} name="Clicks" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[260px] flex flex-col items-center justify-center text-slate gap-2">
                  <TrendingUp size={28} className="opacity-20" />
                  <p className="text-sm">No activity data yet</p>
                </div>
              )}
            </div>

            {/* Rate bars + pie */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
              {/* Rate bars */}
              <div className="bg-white rounded-2xl border border-black/[0.06] p-5 sm:p-6">
                <h2 className="font-bold text-ink mb-5">Key Rates</h2>
                <div className="space-y-5">
                  {rates.map((rate) => (
                    <div key={rate.label}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="font-medium text-ink">{rate.label}</span>
                        <span className="font-bold">{rate.value.toFixed(1)}%</span>
                      </div>
                      <div className="h-2.5 bg-black/[0.04] rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{ width: `${Math.min(rate.value, 100)}%`, background: rate.color }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pie */}
              <div className="bg-white rounded-2xl border border-black/[0.06] p-5 sm:p-6">
                <h2 className="font-bold text-ink mb-5">Campaign Status</h2>
                {pieData.length > 0 ? (
                  <div className="flex items-center justify-between gap-4">
                    <ResponsiveContainer width={160} height={160}>
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={48} outerRadius={72} dataKey="value" stroke="none">
                          {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-3 flex-1">
                      {pieData.map((d) => (
                        <div key={d.name} className="flex items-center gap-3">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: d.color }} />
                          <span className="text-sm text-slate flex-1">{d.name}</span>
                          <span className="text-sm font-bold text-ink">{d.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="h-40 flex items-center justify-center text-slate text-sm">No campaign data</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Campaigns tab */}
        {tab === "Campaigns" && (
          <div>
            {campsLoading ? (
              <Spinner className="py-20" />
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden sm:block bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-black/[0.06]">
                          {["Campaign","Status","Sent","Opens","Clicks","Open Rate","Click Rate"].map((h) => (
                            <th key={h} className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {allCamps.map((c) => {
                          const sent   = Number(c.sent_count  || 0);
                          const opens  = Number(c.open_count  || 0);
                          const clicks = Number(c.click_count || 0);
                          return (
                            <tr key={c.id}
                              className="border-b border-black/[0.04] hover:bg-black/[0.01] cursor-pointer transition-colors"
                              onClick={() => setDrawerCamp(c)}>
                              <td className="px-5 py-3.5 font-semibold text-ink max-w-[200px] truncate">{c.name}</td>
                              <td className="px-5 py-3.5"><StatusBadge status={c.status} /></td>
                              <td className="px-5 py-3.5 text-slate">{sent > 0 ? sent.toLocaleString() : "—"}</td>
                              <td className="px-5 py-3.5 text-slate">{opens > 0 ? opens.toLocaleString() : "—"}</td>
                              <td className="px-5 py-3.5 text-slate">{clicks > 0 ? clicks.toLocaleString() : "—"}</td>
                              <td className="px-5 py-3.5 font-semibold">{sent > 0 ? `${((opens/sent)*100).toFixed(1)}%` : "—"}</td>
                              <td className="px-5 py-3.5 text-slate">{sent > 0 ? `${((clicks/sent)*100).toFixed(1)}%` : "—"}</td>
                            </tr>
                          );
                        })}
                        {allCamps.length === 0 && (
                          <tr><td colSpan={7} className="px-5 py-14 text-center text-slate text-sm">No sent campaigns yet.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile campaign cards */}
                <div className="sm:hidden space-y-3">
                  {allCamps.map((c) => {
                    const sent  = Number(c.sent_count  || 0);
                    const opens = Number(c.open_count  || 0);
                    const clicks = Number(c.click_count || 0);
                    return (
                      <div key={c.id} className="bg-white rounded-2xl border border-black/[0.06] p-4"
                        onClick={() => setDrawerCamp(c)}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <p className="font-semibold text-ink text-sm flex-1 truncate">{c.name}</p>
                          <StatusBadge status={c.status} />
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-3">
                          {[
                            { label: "Sent",   value: sent > 0 ? sent.toLocaleString() : "—" },
                            { label: "Opens",  value: sent > 0 ? `${((opens/sent)*100).toFixed(1)}%` : "—" },
                            { label: "Clicks", value: sent > 0 ? `${((clicks/sent)*100).toFixed(1)}%` : "—" },
                          ].map(({ label, value }) => (
                            <div key={label} className="bg-black/[0.02] rounded-lg p-2.5 text-center">
                              <p className="text-sm font-bold text-ink">{value}</p>
                              <p className="text-[10px] text-slate mt-0.5">{label}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {allCamps.length === 0 && (
                    <div className="bg-white rounded-2xl border border-black/[0.06] p-12 text-center text-slate text-sm">
                      No campaigns yet.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Campaign detail drawer */}
      {drawerCamp && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setDrawerCamp(null)}>
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div className="relative w-full max-w-md bg-white shadow-2xl overflow-y-auto flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-black/[0.06] px-6 py-4 flex items-center justify-between">
              <h2 className="font-bold text-ink truncate pr-4 flex-1">{drawerCamp.name}</h2>
              <button onClick={() => setDrawerCamp(null)}
                className="p-2 rounded-xl text-slate hover:text-ink hover:bg-black/[0.04] transition-colors">
                <span className="text-lg leading-none">×</span>
              </button>
            </div>
            <div className="p-6 space-y-5 flex-1">
              <StatusBadge status={drawerCamp.status} />
              {drawerCamp.subject && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate mb-1">Subject</p>
                  <p className="text-sm text-ink">{drawerCamp.subject}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Sent",   value: Number(drawerCamp.sent_count  || 0).toLocaleString() },
                  { label: "Opens",  value: Number(drawerCamp.open_count  || 0).toLocaleString() },
                  { label: "Clicks", value: Number(drawerCamp.click_count || 0).toLocaleString() },
                  { label: "Bounces",value: Number(drawerCamp.bounce_count|| 0).toLocaleString() },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-black/[0.02] rounded-xl p-4">
                    <p className="text-xl font-bold text-ink">{value}</p>
                    <p className="text-xs text-slate uppercase tracking-wider mt-1">{label}</p>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate mb-1">Created</p>
                <p className="text-sm text-ink">
                  {new Date(drawerCamp.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                </p>
              </div>
              <Link to="/campaigns" className="flex items-center gap-2 text-sm font-semibold text-coral hover:underline">
                View in Campaigns <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
